import type { PortfolioAdminCard } from '@repo/api-contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/shared/ui';
import { useDeletePortfolio, useSetPortfolioHome, useSetPortfolioVisibility } from '../model';

export const PortfolioAdminRow = ({
  item,
  onRefresh,
}: {
  item: PortfolioAdminCard;
  onRefresh: () => void;
}) => {
  const visibility = useSetPortfolioVisibility();
  const home = useSetPortfolioHome();
  const deletion = useDeletePortfolio();
  const [saved, setSaved] = useState<PortfolioAdminCard | null>(null);
  const [deleted, setDeleted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const current = saved && saved.updatedAt > item.updatedAt ? saved : item;
  const busy = visibility.isPending || home.isPending || deletion.isPending;
  const error = visibility.error ?? home.error ?? deletion.error;
  const clear = () => {
    visibility.reset();
    home.reset();
    deletion.reset();
    setMessage(null);
  };
  const setVisibility = async () => {
    if (busy) return;
    clear();
    const result = await visibility.setVisibility({
      id: current.id,
      expectedUpdatedAt: current.updatedAt,
      isVisible: !current.isVisible,
    });
    if (result) {
      setSaved({ ...current, isVisible: !current.isVisible, updatedAt: result.updatedAt });
      setMessage('공개 설정을 저장했습니다.');
    }
  };
  const setHome = async () => {
    if (busy) return;
    clear();
    const result = await home.setHome({
      id: current.id,
      expectedUpdatedAt: current.updatedAt,
      selected: !current.homeSelected,
    });
    if (result) {
      setSaved({ ...current, homeSelected: !current.homeSelected, updatedAt: result.updatedAt });
      setMessage('홈 선택을 저장했습니다.');
    }
  };
  const remove = async () => {
    if (busy || !window.confirm('삭제한 포트폴리오는 복구할 수 없습니다. 삭제하시겠습니까?'))
      return;
    clear();
    if (await deletion.deletePortfolio({ id: current.id, expectedUpdatedAt: current.updatedAt }))
      setDeleted(true);
  };
  if (deleted) return null;
  return (
    <li className="bg-background p-4 transition-colors hover:bg-muted/20">
      <div className="flex flex-wrap items-center gap-4">
        <img
          src={current.imageUrl}
          alt={current.title}
          className="h-14 w-16 rounded-md object-cover"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{current.title}</h3>
          {current.clientName ? (
            <p className="text-sm text-muted-foreground">{current.clientName}</p>
          ) : null}
        </div>
        <div className="flex w-44 flex-wrap gap-1 text-xs text-muted-foreground">
          {current.categories.map((category) => (
            <span key={category.id} className="rounded-full bg-muted px-2 py-1">
              {category.name}
            </span>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            aria-label={`${current.title} 공개`}
            checked={current.isVisible}
            disabled={busy}
            onChange={() => void setVisibility()}
          />
          공개
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            aria-label={`${current.title} 홈 노출`}
            checked={current.homeSelected}
            disabled={busy}
            onChange={() => void setHome()}
          />
          홈 노출
        </label>
        <Link
          className="text-sm underline"
          aria-label={`${current.title} 수정`}
          to={`/portfolio/${current.id}/edit`}
        >
          수정
        </Link>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          aria-label={`${current.title} 삭제`}
          onClick={() => void remove()}
        >
          삭제
        </Button>
      </div>
      {error ? (
        <div className="mt-2 text-sm text-destructive">
          <p role="alert">{error}</p>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onRefresh}>
            목록 새로고침
          </Button>
        </div>
      ) : null}
      {message ? (
        <p role="status" className="mt-2 text-sm">
          {message}
        </p>
      ) : null}
    </li>
  );
};
